import { useEffect, useState } from "react";
import { useRedirect } from "ra-core";
import { ResponsiveBar } from "@nivo/bar";
import { BarChart2, TrendingUp, Users, ShoppingBag, DollarSign, TrendingDown } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

type MonthlyData = { month: string; USD: number; EUR: number; count: number };
type MonthlyPnL = { month: string; 收入: number; 成本: number; 毛利: number };
type TopCustomer = { name: string; company: string; total_usd: number; orders: number; vip_level: string };
type FunnelData = { label: string; count: number; rate?: string };

const VIP_BADGE: Record<string, string> = {
  diamond:  "bg-sky-100 text-sky-700",
  platinum: "bg-purple-100 text-purple-800",
  gold:     "bg-yellow-100 text-yellow-800",
  silver:   "bg-gray-100 text-gray-700",
  standard: "bg-slate-100 text-slate-600",
};

const PERIOD_OPTIONS = [
  { value: "3", label: "近 3 个月" },
  { value: "6", label: "近 6 个月" },
  { value: "12", label: "近 12 个月" },
];

const TABS = [
  { key: "overview", label: "概览" },
  { key: "profit", label: "利润分析" },
];

const SalesReportPage = () => {
  const redirect = useRedirect();
  const [period, setPeriod] = useState("6");
  const [tab, setTab] = useState("overview");
  const [monthly, setMonthly] = useState<MonthlyData[]>([]);
  const [monthlyPnL, setMonthlyPnL] = useState<MonthlyPnL[]>([]);
  const [funnel, setFunnel] = useState<FunnelData[]>([]);
  const [topCustomers, setTopCustomers] = useState<TopCustomer[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const supabase = getSupabaseClient();
    const months = parseInt(period);
    const since = new Date();
    since.setMonth(since.getMonth() - months);
    const sinceStr = since.toISOString();

    setLoading(true);

    Promise.all([
      supabase
        .from("orders")
        .select("id, total, currency, factory_cost, created_at, status, contact_id")
        .gte("created_at", sinceStr)
        .neq("status", "cancelled"),
      supabase
        .from("inquiries")
        .select("id, status, created_at")
        .gte("created_at", sinceStr),
      supabase
        .from("quotations")
        .select("id, status, created_at")
        .gte("created_at", sinceStr),
      supabase
        .from("contacts")
        .select("id, first_name, last_name, total_spent_usd, vip_level, companies(name)")
        .order("total_spent_usd", { ascending: false })
        .limit(10),
    ]).then(([ordersRes, inquiriesRes, quotationsRes, contactsRes]) => {
      const orders = ordersRes.data ?? [];
      const inquiries = inquiriesRes.data ?? [];
      const quotations = quotationsRes.data ?? [];
      const contacts = contactsRes.data ?? [];

      // Monthly revenue aggregation
      const byMonth: Record<string, MonthlyData> = {};
      orders.forEach((o) => {
        const m = o.created_at.slice(0, 7);
        if (!byMonth[m]) byMonth[m] = { month: m, USD: 0, EUR: 0, count: 0 };
        if (o.currency === "EUR") byMonth[m].EUR += o.total ?? 0;
        else byMonth[m].USD += o.total ?? 0;
        byMonth[m].count += 1;
      });
      const sortedMonths = Object.values(byMonth).sort((a, b) => a.month.localeCompare(b.month));
      setMonthly(sortedMonths);

      // Monthly P&L (USD only, factory_cost on order)
      const pnlByMonth: Record<string, { revenue: number; cost: number }> = {};
      orders
        .filter((o) => o.currency === "USD")
        .forEach((o) => {
          const m = o.created_at.slice(0, 7);
          if (!pnlByMonth[m]) pnlByMonth[m] = { revenue: 0, cost: 0 };
          pnlByMonth[m].revenue += o.total ?? 0;
          pnlByMonth[m].cost += o.factory_cost ?? 0;
        });
      const pnlRows: MonthlyPnL[] = Object.entries(pnlByMonth)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, d]) => ({
          month: month.slice(5),
          收入: Math.round(d.revenue),
          成本: Math.round(d.cost),
          毛利: Math.round(d.revenue - d.cost),
        }));
      setMonthlyPnL(pnlRows);

      // Funnel
      const inqCount = inquiries.length;
      const quoteCount = quotations.length;
      const confirmedQuotes = quotations.filter((q) =>
        ["confirmed", "converted"].includes(q.status),
      ).length;
      const orderCount = orders.length;
      const completedCount = orders.filter((o) => o.status === "completed").length;
      setFunnel([
        { label: "询盘", count: inqCount },
        { label: "报价单", count: quoteCount, rate: inqCount ? `${Math.round((quoteCount / inqCount) * 100)}%` : "—" },
        { label: "确认报价", count: confirmedQuotes, rate: quoteCount ? `${Math.round((confirmedQuotes / quoteCount) * 100)}%` : "—" },
        { label: "订单", count: orderCount, rate: confirmedQuotes ? `${Math.round((orderCount / confirmedQuotes) * 100)}%` : "—" },
        { label: "已完成", count: completedCount, rate: orderCount ? `${Math.round((completedCount / orderCount) * 100)}%` : "—" },
      ]);

      // Top customers
      const top: TopCustomer[] = contacts
        .filter((c) => (c.total_spent_usd ?? 0) > 0)
        .map((c) => ({
          name: `${c.first_name} ${c.last_name}`,
          company: (c.companies as unknown as { name: string } | null)?.name ?? "—",
          total_usd: c.total_spent_usd ?? 0,
          orders: orders.filter((o) => o.contact_id === c.id).length,
          vip_level: c.vip_level ?? "standard",
        }));
      setTopCustomers(top);

      setLoading(false);
    });
  }, [period]);

  const totalRevenue = monthly.reduce((s, m) => s + m.USD + m.EUR, 0);
  const totalOrders = monthly.reduce((s, m) => s + m.count, 0);
  const totalCost = monthlyPnL.reduce((s, m) => s + m.成本, 0);
  const totalProfit = monthlyPnL.reduce((s, m) => s + m.毛利, 0);
  const margin = totalRevenue > 0 ? Math.round((totalProfit / totalRevenue) * 100) : 0;

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart2 className="w-6 h-6" /> 销售报表
          </h1>
          <p className="text-sm text-muted-foreground mt-1">营收趋势 · 转化漏斗 · 利润分析</p>
        </div>
        <div className="flex gap-1 border rounded-lg overflow-hidden">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                period === p.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-muted animate-pulse rounded-lg" />
          ))}
        </div>
      ) : tab === "overview" ? (
        <>
          {/* KPI summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={TrendingUp} label="总营收" value={`$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="bg-blue-50 text-blue-600" />
            <KpiCard icon={ShoppingBag} label="订单数" value={String(totalOrders)} color="bg-green-50 text-green-600" />
            <KpiCard icon={Users} label="重点客户" value={String(topCustomers.length)} color="bg-purple-50 text-purple-600" />
            <KpiCard
              icon={BarChart2}
              label="平均客单价"
              value={totalOrders ? `$${Math.round(totalRevenue / totalOrders).toLocaleString()}` : "—"}
              color="bg-orange-50 text-orange-600"
            />
          </div>

          {/* Revenue chart */}
          {monthly.length > 0 && (
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-4">月度营收</h2>
              <div className="h-56">
                <ResponsiveBar
                  data={monthly.map((m) => ({
                    month: m.month.slice(5),
                    USD: Math.round(m.USD),
                    EUR: Math.round(m.EUR),
                  }))}
                  keys={["USD", "EUR"]}
                  indexBy="month"
                  groupMode="stacked"
                  margin={{ top: 10, right: 20, bottom: 40, left: 60 }}
                  padding={0.3}
                  colors={["#3b82f6", "#a855f7"]}
                  borderRadius={3}
                  axisBottom={{ tickSize: 0, tickPadding: 8 }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 8,
                    format: (v: number) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`),
                  }}
                  enableLabel={false}
                  enableGridX={false}
                  theme={{
                    axis: { ticks: { text: { fontSize: 11, fill: "#888" } } },
                    grid: { line: { stroke: "#f0f0f0" } },
                  }}
                  legends={[{ dataFrom: "keys", anchor: "bottom-right", direction: "row", translateY: 38, itemWidth: 60, itemHeight: 16, symbolSize: 10, symbolShape: "square" }]}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Funnel */}
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-4">转化漏斗</h2>
              <div className="space-y-2">
                {funnel.map((f, i) => (
                  <div key={f.label} className="flex items-center gap-3">
                    <div
                      className="h-8 bg-primary/10 rounded flex items-center px-3 text-sm font-medium text-primary transition-all"
                      style={{ width: `${Math.max(20, (f.count / (funnel[0]?.count || 1)) * 100)}%` }}
                    >
                      {f.count}
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm">{f.label}</span>
                      {f.rate && i > 0 && (
                        <span className="ml-2 text-xs text-muted-foreground">({f.rate} 转化)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Top customers */}
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-4">客户排行</h2>
              {topCustomers.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">暂无数据</p>
              ) : (
                <div className="space-y-2">
                  {topCustomers.slice(0, 8).map((c, i) => (
                    <div key={c.name} className="flex items-center gap-3 text-sm">
                      <span className="text-xs text-muted-foreground w-5 text-right">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.company}</div>
                      </div>
                      <span className={`text-xs px-1.5 py-0.5 rounded capitalize ${VIP_BADGE[c.vip_level] ?? VIP_BADGE.standard}`}>
                        {c.vip_level}
                      </span>
                      <span className="font-semibold tabular-nums">
                        ${c.total_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* ── Profit Analysis Tab ─────────────────────────────────────────── */
        <>
          {/* P&L KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={TrendingUp} label="总营收 (USD)" value={`$${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="bg-blue-50 text-blue-600" />
            <KpiCard icon={TrendingDown} label="总成本 (USD)" value={`$${totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color="bg-red-50 text-red-600" />
            <KpiCard icon={DollarSign} label="毛利润 (USD)" value={`$${totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} color={totalProfit >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"} />
            <KpiCard icon={BarChart2} label="毛利率" value={`${margin}%`} color={margin >= 20 ? "bg-green-50 text-green-600" : "bg-orange-50 text-orange-600"} />
          </div>

          {/* P&L chart */}
          {monthlyPnL.length > 0 ? (
            <div className="bg-card border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-1">月度收入 / 成本 / 毛利（USD）</h2>
              <p className="text-xs text-muted-foreground mb-4">基于每笔订单的工厂成本（factory_cost）计算</p>
              <div className="h-64">
                <ResponsiveBar
                  data={monthlyPnL}
                  keys={["收入", "成本", "毛利"]}
                  indexBy="month"
                  groupMode="grouped"
                  margin={{ top: 10, right: 20, bottom: 50, left: 70 }}
                  padding={0.25}
                  colors={["#3b82f6", "#ef4444", "#22c55e"]}
                  borderRadius={3}
                  axisBottom={{ tickSize: 0, tickPadding: 8 }}
                  axisLeft={{
                    tickSize: 0,
                    tickPadding: 8,
                    format: (v: number) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`),
                  }}
                  enableLabel={false}
                  enableGridX={false}
                  theme={{
                    axis: { ticks: { text: { fontSize: 11, fill: "#888" } } },
                    grid: { line: { stroke: "#f0f0f0" } },
                  }}
                  legends={[{ dataFrom: "keys", anchor: "bottom-right", direction: "row", translateY: 44, itemWidth: 60, itemHeight: 16, symbolSize: 10, symbolShape: "square" }]}
                />
              </div>
            </div>
          ) : (
            <div className="bg-card border rounded-lg p-12 text-center text-muted-foreground">
              <BarChart2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>暂无利润数据</p>
              <p className="text-xs mt-1">请在订单中录入工厂成本（factory_cost）后查看</p>
            </div>
          )}

          {/* Per-order profit table */}
          <div className="bg-card border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b">
              <h2 className="text-sm font-semibold">月度利润明细</h2>
            </div>
            {monthlyPnL.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">暂无数据</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 text-xs text-muted-foreground uppercase">
                    <th className="text-left px-5 py-2.5">月份</th>
                    <th className="text-right px-4 py-2.5">营收</th>
                    <th className="text-right px-4 py-2.5">成本</th>
                    <th className="text-right px-4 py-2.5">毛利</th>
                    <th className="text-right px-5 py-2.5">毛利率</th>
                  </tr>
                </thead>
                <tbody>
                  {[...monthlyPnL].reverse().map((row) => {
                    const r = row.收入 > 0 ? Math.round((row.毛利 / row.收入) * 100) : 0;
                    return (
                      <tr key={row.month} className="border-t hover:bg-muted/20">
                        <td className="px-5 py-3 font-medium">{row.month}</td>
                        <td className="px-4 py-3 text-right tabular-nums">${row.收入.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-red-600">${row.成本.toLocaleString()}</td>
                        <td className={`px-4 py-3 text-right tabular-nums font-medium ${row.毛利 >= 0 ? "text-green-600" : "text-red-600"}`}>
                          ${row.毛利.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${r >= 20 ? "bg-green-100 text-green-700" : r >= 10 ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-700"}`}>
                            {r}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const KpiCard = ({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  color: string;
}) => (
  <div className="bg-card border rounded-lg p-4 flex items-center gap-3">
    <div className={`rounded-lg p-2 shrink-0 ${color}`}>
      <Icon className="w-4 h-4" />
    </div>
    <div>
      <div className="text-xl font-bold tabular-nums">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  </div>
);

export default SalesReportPage;
